import type { GridState } from 'ag-grid-community';
import { sanitizeGridState } from '../../../data-models/adapters/legacyViewConfigAdapter';
import { COMPANY_VIEW_TYPE, VIEW_TYPE } from '../../../data-models/view-config.data-model';
import type { ViewModel } from '../../../models/view.model';
import type { ColumnMeta } from '../../../types';
import { CompanyNameAndLogo } from '../components/OverviewTable/columns/PredefinedColumns/CompanyNameAndLogo/CompanyNameAndLogo';
import { PredefinedColumns } from '../components/OverviewTable/columns/PredefinedColumns/PredefinedColumns';

// The whitelist/baseline only reads name + default — non-company grids (security)
// synthesize this from their static colDefs instead of BE column metadata.
export type ColumnVisibilityMeta = Pick<ColumnMeta, 'name' | 'default'>;

// Force the auto group column leftmost: a restored columnOrder omits it, so
// ag-grid mis-positions it right of the pinned company column. No-op without a columnOrder.
const AUTO_GROUP_COL_ID = 'ag-Grid-AutoColumn';

function withAutoGroupColumnFirst(gs: GridState): GridState {
  const ordered = gs.columnOrder?.orderedColIds;
  if (!gs.rowGroup?.groupColIds?.length || !ordered?.length) {
    return gs;
  }
  if (ordered[0] === AUTO_GROUP_COL_ID) {
    return gs;
  }
  const withoutAuto = ordered.filter((id) => id !== AUTO_GROUP_COL_ID);
  return { ...gs, columnOrder: { orderedColIds: [AUTO_GROUP_COL_ID, ...withoutAuto] } };
}

// State handed to ag-grid as `initialState`, stamped with partialColumnState —
// without it, a state lacking an `aggregation` slice (all adapter-built/default
// states) de-registers every value column and group aggregations render empty.
export function initialGridStateFor(
  view: ViewModel,
  columnsMeta?: ColumnVisibilityMeta[],
  onCorruptionDetected?: (slice: string) => void
): GridState | undefined {
  const saved = sanitizeGridState(view.gridState, { onCorruptionDetected });
  const restored = saved
    ? restoreColumnWhitelist(saved, columnsMeta, predefinedColIdsFor(view.companyViewType))
    : defaultGridStateFor(view, columnsMeta);
  const gs = restored ? withAutoGroupColumnFirst(restored) : restored;
  return gs ? { ...gs, partialColumnState: true } : undefined;
}

// Chrome columns (company column, DrillDown chevron) are injected by code and
// never managed via Column Settings — the whitelist must not hide them even
// when a stale saved state predates them.
function predefinedColIdsFor(companyViewType: COMPANY_VIEW_TYPE): Set<string> {
  return new Set(PredefinedColumns.getAllMeta(companyViewType).map((m) => m.name));
}

// ag-grid v35 initialState force-shows any colId missing from columnOrder
// (defaultState.hide=null overrides even colDef hide:true) and drops hiddenColIds
// entries outside that order — so columns added to the tenant after a view's last
// save appear unchosen. Pre-v35 the merge started every column hidden (whitelist);
// restore that: hide unknown columns, appending them to columnOrder so the hide applies.
export function restoreColumnWhitelist(
  gridState: GridState,
  columnsMeta?: ColumnVisibilityMeta[],
  exemptColIds?: ReadonlySet<string>
): GridState {
  if (!columnsMeta || columnsMeta.length === 0) {
    return gridState;
  }

  const ordered = gridState.columnOrder?.orderedColIds;
  if (!ordered?.length) {
    // Absent OR empty columnOrder ⇒ no usable column enumeration (legacy
    // rowGroup/filter-only views, or a corrupt/stripped order). Recover the
    // pre-v35 default — only the curated `c.default` subset — rather than
    // whitelist-hiding everything and blanking the grid. A degenerate empty
    // columnOrder slice is dropped so ag-grid never sees it.
    const base = ordered ? { ...gridState, columnOrder: undefined } : gridState;
    const hidden = unionColIds(
      gridState.columnVisibility?.hiddenColIds,
      columnsMeta.filter((c) => !c.default).map((c) => c.name)
    );
    return hidden.length > 0 ? { ...base, columnVisibility: { hiddenColIds: hidden } } : base;
  }

  const known = new Set(ordered);
  const unknown = columnsMeta
    .map((c) => c.name)
    .filter((name) => !known.has(name) && !exemptColIds?.has(name));
  if (unknown.length === 0) {
    return gridState;
  }

  return {
    ...gridState,
    columnVisibility: {
      hiddenColIds: unionColIds(gridState.columnVisibility?.hiddenColIds, unknown),
    },
    columnOrder: { orderedColIds: [...ordered, ...unknown] },
  };
}

const DEFAULT_VIEW_IDS = {
  overview: 'overview',
  return_forecast: 'return_forecast',
} as const;

/**
 * Default initial GridState for views that have no user-saved gridState.
 *
 * Two layers compose:
 * 1. **Baseline visibility** (from `columnsMeta`, optional): when supplied,
 *    every column where `c.default === false` is added to
 *    `columnVisibility.hiddenColIds`. This preserves the pre-FD-2977
 *    behavior where `getDefaultColumnsDef` only showed the curated
 *    `c.default` subset for any view that had no saved column state.
 * 2. **Per-viewId specifics** (sort / rowGroup / additional hidden cols):
 *    encoded inline for the three built-in default views. Anything
 *    they specify wins over the baseline; hidden lists are unioned.
 *
 * Returns `undefined` only when neither layer contributes anything —
 * i.e. no columnsMeta passed AND the view isn't one of the three defaults.
 */
export function defaultGridStateFor(
  view: ViewModel,
  columnsMeta?: ColumnVisibilityMeta[]
): GridState | undefined {
  const baseline = baselineVisibility(columnsMeta);
  const specifics = perViewIdSpecifics(view);

  if (!baseline && !specifics) {
    return undefined;
  }
  if (!baseline) {
    return specifics;
  }
  if (!specifics) {
    return baseline;
  }

  // Merge: specifics override sort / rowGroup; hiddenColIds are unioned.
  const mergedHidden = unionColIds(
    baseline.columnVisibility?.hiddenColIds,
    specifics.columnVisibility?.hiddenColIds
  );
  return {
    ...baseline,
    ...specifics,
    columnVisibility: mergedHidden.length > 0 ? { hiddenColIds: mergedHidden } : undefined,
  };
}

function baselineVisibility(columnsMeta: ColumnVisibilityMeta[] | undefined): GridState | undefined {
  if (!columnsMeta || columnsMeta.length === 0) {
    return undefined;
  }
  const hidden = columnsMeta.filter((c) => !c.default).map((c) => c.name);
  if (hidden.length === 0) {
    return undefined;
  }
  return { columnVisibility: { hiddenColIds: hidden } };
}

function perViewIdSpecifics(view: ViewModel): GridState | undefined {
  // Round Tracker groups by company for every RT view (default or duplicated), so
  // key on companyViewType above the isDefault gate.
  if (view.companyViewType === COMPANY_VIEW_TYPE.ROUND_TRACKER) {
    return {
      rowGroup: { groupColIds: ['Company Name'] },
      columnVisibility: { hiddenColIds: ['Company Name'] },
      sort: { sortModel: [{ colId: 'TransactionDate', sort: 'asc' }] },
    };
  }

  if (!view.isDefault) {
    return undefined;
  }

  if (view.viewId === DEFAULT_VIEW_IDS.overview) {
    return {
      sort: { sortModel: [{ colId: 'FMV', sort: 'desc' }] },
    };
  }

  if (view.viewId === DEFAULT_VIEW_IDS.return_forecast) {
    return {
      rowGroup: { groupColIds: ['Fund', 'SeedCore', 'InvestmentStatus'] },
      columnVisibility: { hiddenColIds: ['Fund', 'SeedCore', 'InvestmentStatus'] },
      sort: { sortModel: [{ colId: CompanyNameAndLogo.NAME, sort: 'asc' }] },
    };
  }

  return undefined;
}

function unionColIds(a: string[] | undefined, b: string[] | undefined): string[] {
  if (!a && !b) {
    return [];
  }
  if (!a) {
    return [...(b as string[])];
  }
  if (!b) {
    return [...a];
  }
  const seen = new Set(a);
  const merged = [...a];
  for (const id of b) {
    if (!seen.has(id)) {
      merged.push(id);
      seen.add(id);
    }
  }
  return merged;
}

// ag-grid `isGroupOpenByDefault`: restores saved expansion per node (initialState.rowGroupExpansion
// is dropped on async data). Match by node.id (ag35) or node.key (legacy); seeded COMPANY views default-expand.
export function isRowGroupExpandedByDefault(
  view: Pick<ViewModel, 'gridState' | 'type'>,
  node: { id?: string; key?: string | null }
): boolean {
  const expandedIds = view.gridState?.rowGroupExpansion?.expandedRowGroupIds;
  if (expandedIds?.length) {
    return (
      (node.id != null && expandedIds.includes(node.id)) ||
      (node.key != null && expandedIds.includes(node.key))
    );
  }
  return view.type === VIEW_TYPE.COMPANY && !view.gridState;
}
